import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { v2GetPortfolioOutreachStatusOptions } from '@workspace/api-backend/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowUpRight, Check, Pencil } from 'lucide-react'
import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useState } from 'react'
import { trackEvent } from '../../../lib/gtag'
import { useInvalidateHomeState } from '../../home/hooks/use-home-state'
import { outreachApi, portfolioMessagesQueryKey } from '../api'
import { LINKEDIN_SAFETY_HELP_URL } from '../config/launch-checklist'
import {
  estimateMsg1LinkLength,
  estimateMsg1SentLength,
  MSG1_MAX_CHARS,
} from '../config/message-limits'
import { useResetPortfolioMessageMutation } from '../hooks/use-reset-outreach-message-mutation'
import { resolveShownArm } from '../lib/message-variants'
import type { SequenceValidity } from '../lib/review-validity'
import type { OutreachMessageVariant } from '../types'
import {
  decorateLegacyOutreachPreview,
  isLegacyOutreachField,
  type OutreachMessageField,
  renderTemplatePreview,
} from '../utils/render-template-preview'
import { MessagesPreparing } from './messages-preparing'
import { VariantToggle } from './outreach-variant-toggle'
import { WarmupSteps } from './outreach-warmup-steps'

// Fallback seeds (self-contained templates with the [FirstName]/[FundName]/[Link]
// tokens inline) shown before the real per-startup messages generate.
const DEFAULT_MSG1 =
  "Hey [FirstName], congrats on the [FundName] round. I'm raising and [FundName] looks like a strong fit. Open to a quick founder-to-founder take, and an intro if it makes sense? Our full picture in one link: [Link]"
const DEFAULT_MSG2 =
  "Thanks [FirstName], really appreciate it. To make it dead easy, here's a blurb you could forward to [FundName]. Our full picture in one link: [Link]. Zero pressure if the fit or timing isn't right."
const DEFAULT_MSG3 =
  "Hey [FirstName], no worries if now isn't the moment. If it ever becomes easy to point me to the right person at [FundName], I'd be grateful. Good luck with everything!"

type Drafts = { msg1: string; msg2: string; msg3: string }
type MessageKey = keyof Drafts

// The portfolio "Review your messages" step, styled to match the investor
// conversation preview: the three-message sequence rendered as the chat each
// portfolio founder receives. [FundName]/[firstName] tokens are highlighted in
// the preview and resolved per-founder at send time. The primary action saves
// any edits AND marks the messages reviewed.
export function PortfolioReviewMessagesDialog({
  startupId,
  open,
  onOpenChange,
}: {
  startupId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const statusQuery = useQuery(v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }))
  const reviewed =
    statusQuery.data?.status === 'ok' ? statusQuery.data.data.messagesReviewedAt != null : false

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-b border-border/40 px-6 py-5 pr-12 text-left">
          <DialogTitle>
            {reviewed ? 'Edit your warm intro messages' : 'Review your messages'}
          </DialogTitle>
          <DialogDescription>
            This three-message sequence goes to every portfolio founder, with{' '}
            <span className="font-medium text-foreground">[FundName]</span> replaced by their
            investor's fund name. Edit anything; nothing sends without your approval.
          </DialogDescription>
          <a
            href={LINKEDIN_SAFETY_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:text-primary/80"
          >
            What SeedForge does with your LinkedIn
            <ArrowUpRight className="size-3" />
          </a>
        </DialogHeader>
        {open && <PortfolioReviewBody startupId={startupId} />}
      </DialogContent>
    </Dialog>
  )
}

export interface PortfolioReviewBodyHandle {
  approve: () => Promise<void>
  canApprove: boolean
}

// Exported so the unified review-messages modal can embed it as the
// "Portfolio founders" tab without duplicating the editor.
export const PortfolioReviewBody = forwardRef<
  PortfolioReviewBodyHandle,
  { startupId: string; hidden?: boolean; onValidityChange?: (valid: SequenceValidity) => void }
>(function PortfolioReviewBody({ startupId, hidden, onValidityChange }, ref) {
  const queryClient = useQueryClient()
  const messagesQuery = useQuery({
    queryKey: portfolioMessagesQueryKey(startupId),
    // `true` = fill any missing versions. Only this dialog asks; the page's
    // reads must not generate, or an arm becomes sendable off a page view.
    queryFn: () => outreachApi.portfolioGetMessages(startupId, true),
    // This body stays MOUNTED while the dialog is closed (its ref feeds the
    // shared approve handler), so without this the generating fetch would run
    // on every render of the outreach page.
    enabled: !hidden,
  })
  const contactsQuery = useQuery({
    queryKey: ['portfolio-contacts', startupId],
    queryFn: () => outreachApi.portfolioGetContacts(startupId),
  })
  const statusQuery = useQuery(v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }))
  const alreadyReviewed =
    statusQuery.data?.status === 'ok' ? statusQuery.data.data.messagesReviewedAt != null : false
  const resetMessage = useResetPortfolioMessageMutation(startupId)
  const [editing, setEditing] = useState<MessageKey | null>(null)
  const [shownArm, setShownArm] = useState<Partial<Record<MessageKey, string>>>({})
  /**
   * Text the founder actually typed — the ONLY thing Save sends. See the twin
   * in the investor dialog: loading a version's text into a shared draft means
   * a founder who peeks at version B and then presses Save has B stored as
   * their own copy, ending the A/B for that message.
   */
  const [edits, setEdits] = useState<Partial<Record<MessageKey, string>>>({})
  const [selectedIdx, setSelectedIdx] = useState(0)

  const fallbackFor: Record<MessageKey, string> = {
    msg1: DEFAULT_MSG1,
    msg2: DEFAULT_MSG2,
    msg3: DEFAULT_MSG3,
  }

  const variantsFor = (field: MessageKey): OutreachMessageVariant[] => {
    const stored = messagesQuery.data?.variants?.[field] ?? []
    if (edits[field] === undefined || stored.some((v) => v.arm === 'custom')) return stored
    return [
      ...stored,
      { id: `local-custom-${field}`, arm: 'custom', text: edits[field] ?? '', promptVersion: null },
    ]
  }

  /** Whether this field carries a founder edit — saved, or typed just now. */
  const hasCustom = (field: MessageKey): boolean =>
    variantsFor(field).some((v) => v.arm === 'custom')

  const effectiveArm = (field: MessageKey): string =>
    resolveShownArm(variantsFor(field), edits[field] !== undefined ? 'custom' : shownArm[field])

  const textFor = (field: MessageKey): string => {
    const arm = effectiveArm(field)
    if (arm === 'custom' && edits[field] !== undefined) return edits[field] ?? ''
    const variant = variantsFor(field).find((v) => v.arm === arm)
    return variant?.text ?? messagesQuery.data?.[field] ?? fallbackFor[field]
  }

  const drafts: Drafts | null =
    messagesQuery.data === undefined
      ? null
      : { msg1: textFor('msg1'), msg2: textFor('msg2'), msg3: textFor('msg3') }

  /** Switch which version is displayed. Never touches what will be saved. */
  /**
   * Go back to the generated copy: forget any unsaved typing and retire the
   * saved custom version server-side (retired, not deleted — sent contacts
   * reference variants by id).
   */
  const revertToGenerated = async (field: MessageKey) => {
    setEdits((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
    setShownArm((prev) => ({ ...prev, [field]: 'control' }))
    if (messagesQuery.data?.variants?.[field]?.some((v) => v.arm === 'custom')) {
      await resetMessage.mutateAsync(field)
    }
  }

  const selectArm = (field: MessageKey, arm: string) => {
    setShownArm((prev) => ({ ...prev, [field]: arm }))
  }

  const invalidateHome = useInvalidateHomeState(startupId)
  const updateMutation = useMutation({
    mutationFn: (body: Drafts) => outreachApi.portfolioUpdateMessages(startupId, body),
  })
  const reviewMutation = useMutation({
    mutationFn: () => outreachApi.portfolioReviewMessages(startupId),
    // Home too: this stamp is what moves the founder off the "review your
    // messages" card, and the sidebar CTA reads the same state.
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
      })
      await invalidateHome()
    },
  })

  const hasEmpty = !drafts?.msg1.trim() || !drafts?.msg2.trim() || !drafts?.msg3.trim()
  const msg1LinkLen = estimateMsg1LinkLength()
  // Token-aware expanded length (matches the investor tab + backend), so the cap
  // counts [FirstName]/[FundName]/[Link] as their substituted sizes.
  const msg1Total = estimateMsg1SentLength(drafts?.msg1 ?? '')
  const msg1Over = msg1Total > MSG1_MAX_CHARS
  // Per-message prompt versions (from portfolio status) drive legacy-vs-v2 preview.
  const portfolioVersions = statusQuery.data?.status === 'ok' ? statusQuery.data.data : null

  const approve = () =>
    new Promise<void>((resolveP, reject) => {
      if (!drafts) {
        reject(new Error('drafts not loaded'))
        return
      }
      const markReviewed = () => {
        if (alreadyReviewed) {
          resolveP()
          return
        }
        reviewMutation.mutate(undefined, {
          onSuccess: () => {
            trackEvent('portfolio_messages_reviewed', { startup_id: startupId })
            resolveP()
          },
          onError: reject,
        })
      }

      // Only what the founder typed — approving is not editing.
      if (Object.keys(edits).length === 0) {
        markReviewed()
        return
      }

      updateMutation.mutate(edits as Drafts, {
        onSuccess: () => {
          trackEvent('portfolio_messages_saved', { startup_id: startupId })
          markReviewed()
        },
        onError: reject,
      })
    })

  // Report validity up so the parent's Save button re-evaluates on every edit
  // (this tab gates Save even while hidden behind the Investors tab).
  // Unknown while the messages load — see the investor twin.
  const valid = messagesQuery.data ? !hasEmpty && !msg1Over : null
  useEffect(() => onValidityChange?.(valid), [onValidityChange, valid])

  // `approve` closes over the founder's unsaved edits, so the handle is keyed on
  // `edits` as well as `drafts` — otherwise the parent's Save button holds a
  // stale closure and submits the previous keystroke's text.
  useImperativeHandle(ref, () => ({ approve, canApprove: !hasEmpty && !msg1Over }), [
    drafts,
    edits,
    hasEmpty,
    msg1Over,
  ])

  if (messagesQuery.isError) {
    return (
      <p className={cn('py-10 text-center text-[13px] text-muted-foreground', hidden && 'hidden')}>
        Couldn't load your messages. Close and try again.
      </p>
    )
  }

  if (!drafts) return <MessagesPreparing hidden={hidden} />

  // The selected founder personalizes the preview: [FundName]/[firstName] are
  // resolved to their fund + first name (as plain text) so the founder sees a
  // real conversation rather than raw tokens.
  const contacts = contactsQuery.data ?? []
  const selected = contacts[Math.min(selectedIdx, Math.max(contacts.length - 1, 0))] ?? null
  const resolve = selected
    ? {
        fundName: selected.fundName || undefined,
        firstName: (selected.name ?? '').trim().split(/\s+/)[0] || 'there',
      }
    : undefined

  const setDraft = (key: MessageKey, value: string) =>
    setEdits((prev) => ({ ...prev, [key]: value }))
  const toggleEdit = (key: MessageKey) => setEditing((prev) => (prev === key ? null : key))

  return (
    <div
      className={cn(
        'grid min-h-0 flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[260px_1fr] md:overflow-hidden',
        hidden && 'hidden'
      )}
    >
      {/* ── Founder switcher ── */}
      <aside className="border-b border-border/40 md:overflow-y-auto md:border-b-0 md:border-r">
        <div className="px-4 pb-2 pt-4 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          Preview as · portfolio founders
        </div>
        {contactsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="sm" />
          </div>
        ) : contacts.length === 0 ? (
          <p className="px-4 pb-4 text-[11px] leading-relaxed text-muted-foreground/80">
            Founder contacts appear here once research finishes.
          </p>
        ) : (
          <div className="flex gap-1.5 overflow-x-auto px-3 pb-3 md:flex-col md:overflow-visible">
            {contacts.map((contact, i) => {
              const isSelected = i === selectedIdx
              return (
                <button
                  key={contact.contactId}
                  type="button"
                  onClick={() => setSelectedIdx(i)}
                  className={cn(
                    'flex w-56 shrink-0 items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors md:w-auto',
                    isSelected
                      ? 'border-primary/50 bg-primary/10'
                      : 'border-transparent hover:bg-card/60'
                  )}
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border/40 bg-background/60 text-[11px] font-bold text-foreground/80">
                    {makeInitials(contact.name ?? '?')}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold text-foreground">
                      {contact.name ?? 'Founder'}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {[contact.title, contact.companyName].filter(Boolean).join(' · ')}
                    </span>
                    {contact.fundName && (
                      <span className="mt-0.5 inline-flex items-center rounded-full border border-primary/25 bg-primary/8 px-1.5 py-0.5 text-[10px] font-semibold text-primary/70">
                        target {contact.fundName}
                      </span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      </aside>

      {/* ── Conversation preview ── */}
      <div className="space-y-3 px-5 py-5 md:overflow-y-auto">
        <WarmupSteps />

        <MessageBubble
          label="Connection request"
          resolve={resolve}
          hint={
            <span
              className={cn(
                'tabular-nums',
                msg1Over ? 'font-semibold text-destructive' : 'text-muted-foreground/70'
              )}
            >
              {msg1Total} / {MSG1_MAX_CHARS}
            </span>
          }
          text={drafts.msg1}
          field="msg1"
          promptVersion={portfolioVersions?.msg1PromptVersion}
          variants={variantsFor('msg1')}
          selectedArm={effectiveArm('msg1')}
          onSelectArm={(arm) => selectArm('msg1', arm)}
          onUseGenerated={hasCustom('msg1') ? () => void revertToGenerated('msg1') : undefined}
          editing={editing === 'msg1'}
          onToggleEdit={() => toggleEdit('msg1')}
          onChange={(value) => setDraft('msg1', value)}
          footer={
            msg1Over
              ? `Over LinkedIn's ${MSG1_MAX_CHARS}-char connection-note cap (the share link takes ${msg1LinkLen} chars). It will be word-truncated when sent.`
              : undefined
          }
        />

        <ConversationDivider tone="accept">
          {resolve
            ? `${resolve.firstName} accepts your request`
            : 'The founder accepts your request'}
        </ConversationDivider>

        <MessageBubble
          label="Follow-up · after they accept"
          resolve={resolve}
          text={drafts.msg2}
          field="msg2"
          promptVersion={portfolioVersions?.msg2PromptVersion}
          variants={variantsFor('msg2')}
          selectedArm={effectiveArm('msg2')}
          onSelectArm={(arm) => selectArm('msg2', arm)}
          onUseGenerated={hasCustom('msg2') ? () => void revertToGenerated('msg2') : undefined}
          editing={editing === 'msg2'}
          onToggleEdit={() => toggleEdit('msg2')}
          onChange={(value) => setDraft('msg2', value)}
        />

        <ConversationDivider tone="muted">If no reply after a few days</ConversationDivider>

        <MessageBubble
          label="Second follow-up"
          resolve={resolve}
          text={drafts.msg3}
          field="msg3"
          promptVersion={portfolioVersions?.msg3PromptVersion}
          variants={variantsFor('msg3')}
          selectedArm={effectiveArm('msg3')}
          onSelectArm={(arm) => selectArm('msg3', arm)}
          onUseGenerated={hasCustom('msg3') ? () => void revertToGenerated('msg3') : undefined}
          editing={editing === 'msg3'}
          onToggleEdit={() => toggleEdit('msg3')}
          onChange={(value) => setDraft('msg3', value)}
        />
      </div>
    </div>
  )
})

function makeInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  )
}

// An outgoing chat bubble that flips into an inline editor — mirrors the
// investor review dialog so both campaigns read identically.
function MessageBubble({
  label,
  hint,
  text,
  field,
  promptVersion,
  editing,
  onToggleEdit,
  onChange,
  variants,
  selectedArm,
  onSelectArm,
  onUseGenerated,
  footer,
  resolve,
}: {
  label: string
  hint?: ReactNode
  text: string
  /** Which touch this bubble renders — drives legacy greeting/link decoration. */
  field: OutreachMessageField
  /** Langfuse prompt version for this touch — decides legacy vs v2 rendering. */
  promptVersion?: string | null
  editing: boolean
  onToggleEdit: () => void
  onChange: (value: string) => void
  /** Every live version of this message; absent or single = no toggle shown. */
  variants?: OutreachMessageVariant[]
  selectedArm?: string
  onSelectArm?: (arm: string) => void
  /** Discard the founder's edit for this field. Absent when there is none. */
  onUseGenerated?: () => void
  footer?: string
  resolve?: { fundName?: string; firstName?: string }
}) {
  // Decorate legacy (tokenless) messages into the new-template shape for the
  // preview so old and new render the same. Legacy-vs-v2 is decided by the prompt
  // VERSION (mirrors the backend push), not the body. The editable draft stays raw.
  const previewText = isLegacyOutreachField(field, promptVersion, 'portfolio')
    ? decorateLegacyOutreachPreview(field, text)
    : text
  return (
    <div className="flex flex-col items-end">
      <div className="mb-1 flex w-full max-w-[88%] items-center justify-between gap-3">
        <span className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground/70">
          {label}
        </span>
        <span className="flex items-center gap-2 text-[11px]">
          {hint}
          {variants && selectedArm && onSelectArm && (
            <VariantToggle
              variants={variants}
              selected={selectedArm}
              onSelect={onSelectArm}
              onDropCustom={editing ? undefined : onUseGenerated}
            />
          )}
          <button
            type="button"
            onClick={onToggleEdit}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium transition-colors',
              editing ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {editing ? (
              <>
                <Check className="size-3" strokeWidth={2.5} />
                Done
              </>
            ) : (
              <>
                <Pencil className="size-3" />
                Edit
              </>
            )}
          </button>
        </span>
      </div>

      <div
        className={cn(
          'w-full max-w-[88%] rounded-2xl rounded-tr-sm border px-4 py-2.5',
          editing ? 'border-primary/50 bg-primary/5' : 'border-primary/20 bg-primary/10'
        )}
      >
        {editing ? (
          <Textarea
            value={text}
            onChange={(event) => onChange(event.target.value)}
            textareaSize="lg"
            autoFocus
            className="border-border/60 bg-background/60 text-[13px] leading-relaxed text-foreground/90"
          />
        ) : (
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/90">
            {text.trim() ? (
              renderTemplatePreview(previewText, resolve ?? {})
            ) : (
              <span className="italic text-muted-foreground/70">No message yet</span>
            )}
          </p>
        )}
      </div>

      {footer && (
        <p className="mt-1 w-full max-w-[88%] text-right text-[11px] leading-snug text-muted-foreground/80">
          {footer}
        </p>
      )}
    </div>
  )
}

function ConversationDivider({
  tone,
  children,
}: {
  tone: 'accept' | 'muted'
  children: ReactNode
}) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-0.5 text-[11.5px] text-muted-foreground">
      {tone === 'accept' && <Check className="size-3 text-emerald-400" strokeWidth={3} />}
      {children}
    </div>
  )
}
